import { ApiResult, caller, urlMaker, appendToFormData, formDataFlat, extractErrorMessage, parseJsonSafe } from '@shared/utils';
import {
  IDeclaration,
  IDeclarationPersistence,
  IDeclarationFormValues,
  IDeclarationPost,
  IUnknownDeclaration,
  IUnknownDeclarationPersistence,
  IPartnerDeclaration,
  IWaybill,
  IWaybillPersistence,
  IProformaInvoice,
  IProformaInvoicePersistence,
  IStatusMapItem,
  IStatusMapItemPersistence,
  IDeclarationCustomsStatus,
  IDeclarationCustomsStatusPersistence,
  IParcelStates,
  IExportDeclarationTask,
  IDeclarationExcel,
  ITinyDeclaration,
  ITinyDeclarationPersistence,
  ICustomsFinChange,
  ICustomsFinChangePersistence,
} from '../interfaces';
import {
  DeclarationMapper,
  UnknownDeclarationMapper,
  TinyDeclarationMapper,
  WaybillMapper,
  ProformaInvoiceMapper,
  DeclarationPostMapper,
  PartnerDeclarationMapper,
  ParcelStatesMapper,
  ReturnDeclarationMapper,
  BulkHandoverMapper,
} from '../mappers';
import { IUnknownDeclarationFormValues } from '../interfaces';

const finChangeToDomain = (p: ICustomsFinChangePersistence): ICustomsFinChange => ({
  id: p.id,
  declarationId: p.declaration_id,
  newFin: p.new_fin,
  oldFin: p.old_fin,
  trackingNo: p.tracking_no,
  step: p.step,
  status: p.status,
  source: p.source,
  attempts: p.attempts,
  nextRetryAt: p.next_retry_at,
  lastStatusCode: p.last_status_code,
  lastErrorCode: p.last_error_code,
  lastError: p.last_error,
  causerName: [p.causer_name, p.causer_surname].filter(Boolean).join(' ') || null,
  completedAt: p.completed_at,
  createdAt: p.created_at,
});

const statusMapToDomain = (s: IStatusMapItemPersistence): IStatusMapItem => ({
  id: s.id,
  descr: s.descr,
  createdAt: s.created_at,
  state: { id: s.state_id, name: s.state_name },
  user: { id: s.user_id, name: s.user_name },
});

export type IHandoverDetails = {
  user: { id: number; name: string };
  ordersPrice: { try: number; azn: number };
  deliveryPrice: { usd: number; azn: number };
  balance: { usd: number; try: number };
  convertedBalance: { usd: number; try: number };
  debt: {
    total: { usd: number; azn: number };
    minimum: { azn: number };
    all: { azn: number; package: number };
  };
};

export type IReturnDetails = {
  id: number;
  deliveryPrice: number;
  productPriceToBeRefunded: number;
  deliveryPriceToBeRefunded: number;
  paid: boolean;
  order: { id: number; amountToBeRefunded: number; amountToBeRefundedExtra: number; amountToBeRefundedWithExtra: number } | null;
};

export const DeclarationsService = {
  getDeclarations: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: IDeclaration[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/v2/declaration/list', {
      page: 1,
      per_page: 20,
      ...query,
    });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(
          200,
          {
            data: (result.data || []).map((d: IDeclarationPersistence) => DeclarationMapper.toDomain(d)),
            total: result.total || 0,
          },
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getDeclarationById: async (id: string | number): Promise<ApiResult<200, IDeclaration> | ApiResult<400 | 500, string>> => {
    const url = urlMaker('/api/admin/declaration/info', { declaration_id: id });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, DeclarationMapper.toDomain(result.data as IDeclarationPersistence), null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getDeclarationByTrackCode: async (params: { trackCode: string; acceptance?: boolean; trendyol?: boolean }): Promise<ApiResult<200, IDeclaration> | ApiResult<400 | 500, string>> => {
    const url = urlMaker('/api/admin/declaration/info_by_track_code', {
      track_code: params.trackCode,
      acceptance: params.acceptance ?? false,
      trendyol: params.trendyol === undefined ? undefined : params.trendyol ? '1' : '0',
    });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, DeclarationMapper.toDomain(result.data as IDeclarationPersistence), null);
      }
      const result = await response.json();
      const trackCodeError = result?.errors?.track_code;
      return new ApiResult(400, trackCodeError ? Object.values(result.errors).flat().join(', ') : 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getPartnerDeclarationByTrackCode: async (params: { trackCode: string }): Promise<ApiResult<200, IDeclaration> | ApiResult<400 | 500, string>> => {
    const url = urlMaker('/api/branchpartner/declarations/info_by_track_code', { track_code: params.trackCode });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, DeclarationMapper.toDomain(result.data as IDeclarationPersistence), null);
      }
      const result = await response.json();
      const trackCodeError = result?.errors?.track_code;
      return new ApiResult(400, trackCodeError ? Object.values(result.errors).flat().join(', ') : 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  createDeclaration: async (values: IDeclarationFormValues, combinedIds?: (string | number)[]): Promise<ApiResult<200, null> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/declaration/create');
    const body = new FormData();
    appendToFormData(DeclarationMapper.toPersistence(values), body);
    appendToFormData(formDataFlat({ combined_id: combinedIds }), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) {
        return new ApiResult(200, null, null);
      }
      const result = await response.json();
      if (response.status === 422) {
        return new ApiResult(422, result.errors || {}, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  updateDeclaration: async (id: string | number, values: IDeclarationFormValues): Promise<ApiResult<200, null> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/declaration/edit');
    const body = new FormData();
    appendToFormData(DeclarationMapper.toPersistence(values, id), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) {
        return new ApiResult(200, null, null);
      }
      const result = await response.json();
      if (response.status === 422) {
        return new ApiResult(422, result.errors || {}, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  cancelDeclarations: async (ids: (string | number)[]): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/cancel', { declaration_id: ids });
    try {
      const response = await caller(url, { method: 'POST' });
      if (response.ok) {
        return new ApiResult(200, null, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  bulkHandover: async (stateId: string, tariffCategoryId: string): Promise<ApiResult<200, null> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/declaration/handover');
    const body = new FormData();
    appendToFormData(BulkHandoverMapper.toPersistence({ stateId, tariffCategoryId }), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      if (response.status === 422) return new ApiResult(422, result.errors || {}, null);
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getPaymentTypes: async (): Promise<ApiResult<200, { id: number; name: string }[]> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/payment_types/list', { per_page: 1000 });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(
          200,
          (result.data || []).map((p: any) => ({ id: p.id, name: p.name })),
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getProductTypes: async (): Promise<ApiResult<200, { id: number; name: string }[]> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/product-types/getlist', { per_page: 1000 });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(
          200,
          (result.data || []).map((p: any) => ({ id: p.id, name: p.name })),
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getPlanCategories: async (): Promise<ApiResult<200, { id: number; name: string }[]> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/tariff/category_list', { per_page: 1000 });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(
          200,
          (result.data || []).map((p: any) => ({ id: p.id, name: p.name })),
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  cancelDispatch: async (id: string | number): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/flights/canceldepesh', { declaration_id: id });
    try {
      const response = await caller(url, { method: 'POST' });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  removeFromFlight: async (id: string | number): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/warehouse/flights/remove_item', { declaration_id: id });
    try {
      const response = await caller(url, { method: 'POST' });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  removeFromContainer: async (id: string | number): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/containers-transfers/remove-from-container', { declaration_id: id });
    try {
      const response = await caller(url, { method: 'POST' });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  toggleWanted: async (id: string | number, descr: string | null): Promise<ApiResult<200, { result: boolean; message?: string }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/wanted/toggle', { declaration_id: id });
    const body = new FormData();
    appendToFormData({ descr }, body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, result, null);
      }
      const result = await response.json();
      return new ApiResult(400, result?.message || 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getOrderIds: async (id: string | number): Promise<ApiResult<200, string[]> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/orders', { declaration_id: id });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, result.data || [], null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getCustomsStatus: async (params: { trackCode: string }): Promise<ApiResult<200, IDeclarationCustomsStatus> | ApiResult<400 | 500, string>> => {
    const url = urlMaker('/api/client/customs_status', { track_code: params.trackCode });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data = result.data as IDeclarationCustomsStatusPersistence;
        return new ApiResult(200, { customsStatus: data?.customs_status ?? null, json: data?.json || {} }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getParcelStates: async (params: { trackCode: string }): Promise<ApiResult<200, IParcelStates[]> | ApiResult<400 | 500, string>> => {
    const url = urlMaker(`/api/admin/declaration/trendyol/parcel-states/${params.trackCode}`);
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data = (result.data || []).map(ParcelStatesMapper.toDomain);
        return new ApiResult(200, data, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  changeTrendyolStatus: async (ids: (string | number)[], statusId: string | number, filter: string | number = 0): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/trendyol/updateState');
    const body = new FormData();
    appendToFormData(formDataFlat({ declaration_id: ids, trendyol_state_id: statusId, filter }), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      return new ApiResult(400, result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getFinChanges: async (declarationId: string | number): Promise<ApiResult<200, ICustomsFinChange[]> | ApiResult<400 | 500, string>> => {
    const url = urlMaker('/api/admin/customs/fin_changes', { declaration_id: declarationId, per_page: 50 });
    let response: Response;
    try {
      response = await caller(url);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
    if (!response.ok) return new ApiResult(400, await extractErrorMessage(response, `Məlumatlar əldə edilə bilmədi (HTTP ${response.status})`), null);
    const result = await parseJsonSafe(response);
    const rows = (result?.data || []) as ICustomsFinChangePersistence[];
    return new ApiResult(200, rows.map(finChangeToDomain), null);
  },

  changeFin: async (declarationId: string | number, pincode: string): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker(`/api/admin/v2/declaration/customs_fin_change/${declarationId}`, { pincode });
    let response: Response;
    try {
      response = await caller(url);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
    if (response.ok) return new ApiResult(200, null, null);
    return new ApiResult(400, await extractErrorMessage(response, `Əməliyyat icra edilmədi (HTTP ${response.status})`), null);
  },

  createInCustoms: async (declarationId: string | number): Promise<ApiResult<200, string> | ApiResult<400, string>> => {
    const url = urlMaker(`/api/admin/v2/declaration/customs_fin_change/${declarationId}/create_in_customs`);
    let response: Response;
    try {
      response = await caller(url, { method: 'POST' });
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
    if (response.ok) {
      const result = await parseJsonSafe(response);
      return new ApiResult(200, (result?.message as string) || 'Bağlama gömrükdə yaradıldı.', null);
    }
    return new ApiResult(400, await extractErrorMessage(response, `Əməliyyat icra edilmədi (HTTP ${response.status})`), null);
  },

  addCommercial: async (values: { declarationId: string; awb: string; voen: string }): Promise<ApiResult<200, null> | ApiResult<422, Record<string, string[]>> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/commercial');
    const body = new FormData();
    appendToFormData({ declaration_id: values.declarationId, awb: values.awb, voen: values.voen }, body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      if (response.status === 400 || response.status === 422) {
        const result = await response.json();
        return new ApiResult(422, result?.errors || {}, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getHandoverDetails: async (
    ids: (string | number)[],
    packages?: { small_package?: string; medium_package?: string; big_package?: string },
  ): Promise<ApiResult<200, IHandoverDetails> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/pay', { declaration_id: ids });
    const body = new FormData();
    if (packages) appendToFormData(packages, body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) {
        const result = await response.json();
        const d = result.data;
        return new ApiResult(
          200,
          {
            user: { id: d.user_id, name: d.user_name },
            ordersPrice: { try: d.values.credit_try, azn: d.converted.credit_try },
            deliveryPrice: { usd: d.values.credit_delivery, azn: d.converted.credit_delivery },
            balance: { usd: d.values.balance_usd, try: d.values.balance_try },
            convertedBalance: { usd: d.converted.balance_usd, try: d.converted.balance_try },
            debt: {
              total: { usd: d.values.credit_usd, azn: d.converted.credit_usd },
              minimum: { azn: d.converted.minimal },
              all: { azn: d.converted.all_credit, package: d.converted.package_amount },
            },
          },
          null,
        );
      }
      const result = await response.json();
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  handover: async (
    ids: (string | number)[],
    data: {
      cash: string;
      terminal: string;
      accepted: boolean;
      handover_task: boolean;
      small_package: string;
      medium_package: string;
      big_package: string;
      confirm: boolean;
    },
  ): Promise<ApiResult<200, { handover_task_id?: number }> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/declaration/pay');
    const body = new FormData();
    appendToFormData(
      formDataFlat({
        declaration_id: ids,
        cash: data.cash,
        terminal: data.terminal,
        accepted: data.accepted,
        handover_task: data.handover_task,
        small_package: data.small_package,
        medium_package: data.medium_package,
        big_package: data.big_package,
        confirm: data.confirm,
      }),
      body,
    );
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, { ...(result.data || {}), handover_task_id: result.handover_task_id ?? result.data?.handover_task_id }, null);
      }
      const result = await response.json();
      if (response.status === 422) return new ApiResult(422, result.errors || {}, null);
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getReturnDetails: async (id: string | number): Promise<ApiResult<200, IReturnDetails> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/returns/run', { declaration_id: id });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const d = result.data;
        return new ApiResult(
          200,
          {
            id: d.declaration.id,
            deliveryPrice: d.declaration.delivery_price ? parseFloat(d.declaration.delivery_price) : 0,
            productPriceToBeRefunded: d.declaration.money_back_price ? parseFloat(String(d.declaration.money_back_price)) : 0,
            deliveryPriceToBeRefunded: d.declaration.money_back ? parseFloat(String(d.declaration.money_back)) : 0,
            paid: !!d.declaration.payed,
            order: d.order?.exist
              ? { id: d.order.id, amountToBeRefunded: d.order.money_back, amountToBeRefundedExtra: d.order.money_back_percent, amountToBeRefundedWithExtra: d.order.money_back_with_percent }
              : null,
          },
          null,
        );
      }
      const result = await response.json();
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  returnDeclaration: async (
    id: string | number,
    data: { typeId: string; returnOrderExtra: boolean; returnDeclarationPrice: boolean; returnDeliveryPrice: boolean },
  ): Promise<ApiResult<200, null> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/returns/run');
    const body = new FormData();
    appendToFormData(formDataFlat(ReturnDeclarationMapper.toPersistence({ declarationId: String(id), ...data })), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      if (response.status === 422 || response.status === 400) return new ApiResult(422, result.errors || {}, null);
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getWaybills: async (id: string | number): Promise<ApiResult<200, IWaybill[]> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/manifesto', { declaration_id: id });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(
          200,
          (result || []).map((w: IWaybillPersistence) => WaybillMapper.toDomain(w)),
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getWaybillsByFlight: async (params: { flightId: string | number; partnerId?: string | number }): Promise<ApiResult<200, IWaybill[]> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/manifesto', { flight_id: params.flightId, partner_id: params.partnerId });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(
          200,
          (result || []).map((w: IWaybillPersistence) => WaybillMapper.toDomain(w)),
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getProformaInvoice: async (ids: (string | number)[]): Promise<ApiResult<200, IProformaInvoice> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/proforma', { declaration_id: ids });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, ProformaInvoiceMapper.toDomain(result as IProformaInvoicePersistence), null);
      }
      const result = await response.json();
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getStatusMap: async (id: string | number): Promise<ApiResult<200, IStatusMapItem[]> | ApiResult<400, string>> => {
    const url = urlMaker('/api/client/get_status_map', { model_id: 2, object_id: id });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(
          200,
          (result || []).map((s: IStatusMapItemPersistence) => statusMapToDomain(s)),
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  // Status change for explicit declaration ids (row action / selected rows).
  updateStatus: async (ids: (string | number)[], statusId: string | number, description?: string): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/edit/state', { declaration_id: ids, state_id: statusId });
    const body = new FormData();
    appendToFormData({ descr: description }, body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  // Filter-based bulk status change — applies to every declaration matching the
  // current table filters, not just the loaded page.
  bulkUpdateStatus: async (query: Record<string, any>, statusId: string | number, description?: string): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/v2/declaration/edit/state', { ...query, new_state_id: statusId, ...(description ? { new_descr: description } : {}) });
    try {
      const response = await caller(url, { method: 'POST' });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Məlumatlar əldə edilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  updateRead: async (ids: (string | number)[], read: boolean): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/isnew', { model_id: 2, object_id: ids, is_new: read });
    try {
      const response = await caller(url, { method: 'POST' });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      const reason = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Əməliyyat uğursuz oldu';
      return new ApiResult(400, reason, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getExcel: async (query: Record<string, any> = {}): Promise<ApiResult<200, Blob> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/export', { ...query, united: 0 });
    try {
      const response = await caller(url);
      if (response.ok) return new ApiResult(200, await response.blob(), null);
      return new ApiResult(400, 'Sənəd hazırlana bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getExcelMini: async (query: Record<string, any> = {}): Promise<ApiResult<200, Blob> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/export/mini', { ...query, united: 0 });
    try {
      const response = await caller(url);
      if (response.ok) return new ApiResult(200, await response.blob(), null);
      return new ApiResult(400, 'Sənəd hazırlana bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getWantedExcel: async (): Promise<ApiResult<200, Blob> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/wanted/export');
    try {
      const response = await caller(url);
      if (response.ok) return new ApiResult(200, await response.blob(), null);
      return new ApiResult(400, 'Sənəd hazırlana bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  combine: async (ids: (string | number)[]): Promise<ApiResult<200, IDeclaration> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/combine', { combined_id: ids });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, DeclarationMapper.toDomain(result.data as IDeclarationPersistence), null);
      }
      const result = await response.json();
      const errors = result?.errors ? Object.values(result.errors).flat().join('. ') : 'Bağlamalar birləşdirilə bilmədi';
      return new ApiResult(400, errors as string, null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  acceptDeclaration: async (id: string | number, data: { wardrobeNumber: string; description: string; updateStatus: boolean }): Promise<ApiResult<200, null> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/declaration/update');
    const body = new FormData();
    appendToFormData(formDataFlat({ declaration_id: id, wardrobe_number: data.wardrobeNumber, descr: data.description, change_state: data.updateStatus }), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      if (response.status === 422) return new ApiResult(422, result.errors || {}, null);
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  importExcel: async (file: File): Promise<ApiResult<200, { message: string }> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/export/declaration/import');
    const body = new FormData();
    appendToFormData({ file }, body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, { message: result.message }, null);
      }
      const result = await response.json();
      if (response.status === 422) return new ApiResult(422, result.errors || {}, null);
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getExportTasks: async (): Promise<ApiResult<200, IExportDeclarationTask[]> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/export/declaration/task_list', { sort_column: 'id', sort_order: 'desc' });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(
          200,
          (result.data || []).map((item: any) => ({
            id: item.id,
            type: item.type,
            filePath: item.file_path,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
            user: { id: item.user?.id, name: item.user?.user_name || '' },
            status: { id: item.status?.id || item.state_id, name: item.status?.name || '' },
            model: { id: item.model?.id || item.model_id, name: item.model?.name || '' },
          })),
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  downloadExportTask: async (id: string | number): Promise<ApiResult<200, Blob> | ApiResult<400, string>> => {
    const url = urlMaker(`/api/admin/export/declaration/download/${id}`);
    try {
      const response = await caller(url);
      if (response.ok) return new ApiResult(200, await response.blob(), null);
      return new ApiResult(400, 'Sənəd hazırlana bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getHandoverExcel: async (query: Record<string, any>): Promise<ApiResult<200, Blob> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/handing_export', query);
    try {
      const response = await caller(url);
      if (response.ok) return new ApiResult(200, await response.blob(), null);
      return new ApiResult(400, 'Sənəd hazırlana bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getCurrentMonthDeclarations: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: IDeclaration[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/this_month', {
      page: 1,
      per_page: 20,
      ...query,
    });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(
          200,
          {
            data: (result.data || []).map((d: IDeclarationPersistence) => DeclarationMapper.toDomain(d)),
            total: result.total || 0,
          },
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getTinyDeclarations: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: ITinyDeclaration[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/minilist', {
      page: 1,
      per_page: 20,
      ...query,
    });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, { data: (result.data || []).map((d: ITinyDeclarationPersistence) => TinyDeclarationMapper.toDomain(d)), total: result.total || 0 }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getDeclarationsExport: async (query: Record<string, any> = {}, signal?: AbortSignal): Promise<ApiResult<200, { data: IDeclarationExcel[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/v2/declaration/list', query);
    try {
      const response = await caller(url, { signal });
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, { data: (result.data || []).map((d: IDeclarationPersistence) => DeclarationMapper.toExcel(d)), total: result.total || 0 }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },
};

export const DeletedDeclarationsService = {
  getList: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: IDeclaration[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/customs/deleteddeclarations', { page: 1, per_page: 20, ...query });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data = (result.data || []).map((d: IDeclarationPersistence) => DeclarationMapper.toDomain(d));
        return new ApiResult(200, { data, total: result.total ?? data.length }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },
};

export const PostDeclarationsService = {
  getList: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: IDeclarationPost[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/post_declarations', { page: 1, per_page: 20, ...query });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data = (result.data || []).map(DeclarationPostMapper.toDomain);
        return new ApiResult(200, { data, total: result.total ?? data.length }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },
};

export const UnknownDeclarationsService = {
  getList: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: IUnknownDeclaration[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/v2/conflicted_declaration/list', { page: 1, per_page: 20, ...query });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data = (result.data || []).map((d: IUnknownDeclarationPersistence) => UnknownDeclarationMapper.toDomain(d));
        return new ApiResult(200, { data, total: result.total ?? data.length }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getById: async (id: string | number): Promise<ApiResult<200, IUnknownDeclaration> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/conflicted_declaration/info', { conflicted_declaration_id: id });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, UnknownDeclarationMapper.toDomain(result.data as IUnknownDeclarationPersistence), null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi.', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  cancel: async (ids: (string | number)[]): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/conflicted_declaration/cancel', { conflicted_declaration_id: ids });
    try {
      const response = await caller(url, { method: 'POST' });
      if (response.ok) return new ApiResult(200, null, null);
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  acceptWithChanges: async (id: string | number, values: IUnknownDeclarationFormValues): Promise<ApiResult<200, null> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/conflicted_declaration/accept');
    const body = new FormData();
    appendToFormData(UnknownDeclarationMapper.toPersistence(values, id), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      if (response.status === 422) return new ApiResult(422, result.errors || {}, null);
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  create: async (values: IUnknownDeclarationFormValues): Promise<ApiResult<200, null> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/conflicted_declaration/create');
    const body = new FormData();
    appendToFormData(UnknownDeclarationMapper.toPersistence(values), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      if (response.status === 422) return new ApiResult(422, result.errors || {}, null);
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  update: async (id: string | number, values: IUnknownDeclarationFormValues): Promise<ApiResult<200, null> | ApiResult<400 | 422, any>> => {
    const url = urlMaker('/api/admin/conflicted_declaration/edit');
    const body = new FormData();
    appendToFormData(UnknownDeclarationMapper.toPersistence(values, id), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      const result = await response.json();
      if (response.status === 422) return new ApiResult(422, result.errors || {}, null);
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },
};

export const PartnerDeclarationsService = {
  getList: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: IPartnerDeclaration[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/declaration/partner_declarations', { page: 1, per_page: 20, ...query });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data = (result.data || []).map(PartnerDeclarationMapper.toDomain);
        return new ApiResult(200, { data, total: result.total ?? data.length }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getExcel: async (query: Record<string, any> = {}, mini = false): Promise<ApiResult<200, Blob> | ApiResult<400, string>> => {
    const url = mini ? urlMaker('/api/admin/declaration/export/mini', { ...query, only_partner: 1 }) : urlMaker('/api/admin/declaration/export', { ...query, only_partner: 1 });
    try {
      const response = await caller(url);
      if (response.ok) {
        const blob = await response.blob();
        return new ApiResult(200, blob, null);
      }
      return new ApiResult(400, 'Export uğursuz oldu', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },
};
